import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useCreateSecurityCenter, useUpdateSecurityCenter } from "@/api/devices";
import type { WindowsSecurityCenter, SecurityCenterStatus } from "@/api/types";

const STATUSES: SecurityCenterStatus[] = ["GOOD", "POOR", "SNOOZED", "NOT_MONITORED", "ERROR"];

function statusLabel(s: string) {
  return s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  securityCenter?: WindowsSecurityCenter | null;
}

const FIELDS: { key: keyof Pick<WindowsSecurityCenter, "firewall" | "autoupdate" | "antivirus" | "internet_setting" | "user_account_control" | "security_center_service">; label: string }[] = [
  { key: "firewall", label: "Firewall" },
  { key: "autoupdate", label: "Auto Update" },
  { key: "antivirus", label: "Antivirus" },
  { key: "internet_setting", label: "Internet Setting" },
  { key: "user_account_control", label: "User Account Control" },
  { key: "security_center_service", label: "Security Center Service" },
];

export function SecurityCenterModal({ open, onClose, tenantId, deviceId, securityCenter }: Props) {
  const isEdit = !!securityCenter;
  const create = useCreateSecurityCenter(tenantId, deviceId);
  const update = useUpdateSecurityCenter(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [values, setValues] = useState<Record<string, SecurityCenterStatus>>({
    firewall: "NOT_MONITORED",
    autoupdate: "NOT_MONITORED",
    antivirus: "NOT_MONITORED",
    internet_setting: "NOT_MONITORED",
    user_account_control: "NOT_MONITORED",
    security_center_service: "NOT_MONITORED",
  });

  useEffect(() => {
    if (open) {
      setValues({
        firewall: securityCenter?.firewall ?? "NOT_MONITORED",
        autoupdate: securityCenter?.autoupdate ?? "NOT_MONITORED",
        antivirus: securityCenter?.antivirus ?? "NOT_MONITORED",
        internet_setting: securityCenter?.internet_setting ?? "NOT_MONITORED",
        user_account_control: securityCenter?.user_account_control ?? "NOT_MONITORED",
        security_center_service: securityCenter?.security_center_service ?? "NOT_MONITORED",
      });
    }
  }, [open, securityCenter]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: securityCenter.id, payload: values }, onSuccess);
    } else {
      create.mutate({ payload: values }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Set Up"} Windows Security Center</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        {FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">{label}</label>
            <Select
              value={values[key]}
              onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value as SecurityCenterStatus }))}
            >
              {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </Select>
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending}>{isPending ? "Saving..." : isEdit ? "Update" : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
